import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import SEO from "@/components/SEO";
import PageHero from "@/components/site/PageHero";
import Reveal from "@/components/site/Reveal";
import { Mail, Phone, MapPin, MessageCircle, Send, Loader2 } from "lucide-react";

const SERVICES = [
  "Website Development", "Custom Web Applications", "Ecommerce Development", "Mobile App Development",
  "AI Automation", "SEO", "Digital Marketing", "Branding", "UI/UX Design", "Other",
];
const BUDGETS = ["< $5k", "$5k – $15k", "$15k – $50k", "$50k+"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", service: "", budget: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      setSent(true);
      setForm({ name: "", email: "", phone: "", company: "", service: "", budget: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "h-12 w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 text-sm text-white placeholder:text-slate-500 transition-colors focus:border-vantage focus:outline-none";

  return (
    <>
      <SEO title="Contact" description="Get in touch with Web Vantage Enterprises. Tell us about your project and we'll respond within 24 hours." path="/contact" />
      <PageHero
        overline="Contact"
        title="Let's start something exceptional"
        subtitle="Share a few details about your project and our team will be in touch within 24 hours."
        crumbs={[{ label: "Contact" }]}
      />
      <section className="py-20 lg:py-28">
        <div className="container-wv grid gap-12 lg:grid-cols-[1fr_1.3fr]">
          {/* info */}
          <Reveal className="space-y-6">
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8">
              <h3 className="font-heading text-xl font-semibold text-white">Talk to us directly</h3>
              <ul className="mt-6 space-y-5 text-sm">
                <li className="flex items-center gap-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-vantage/10 text-vantage"><Mail className="h-5 w-5" /></span>
                  <a href="mailto:hello@webvantagenterprises.com" className="text-slate-300 hover:text-white">hello@webvantagenterprises.com</a>
                </li>
                <li className="flex items-center gap-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-vantage/10 text-vantage"><Phone className="h-5 w-5" /></span>
                  <a href="tel:+16616664529" className="text-slate-300 hover:text-white">+1 (661) 666-4529</a>
                </li>
                <li className="flex items-center gap-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-vantage/10 text-vantage"><MessageCircle className="h-5 w-5" /></span>
                  <a href="https://wa.me/16616664529" target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-white">Chat on WhatsApp</a>
                </li>
                <li className="flex items-start gap-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-vantage/10 text-vantage"><MapPin className="h-5 w-5" /></span>
                  <span className="text-slate-300">100 Market Street, Suite 500<br />San Francisco, CA 94105</span>
                </li>
              </ul>
            </div>
            <div className="overflow-hidden rounded-3xl border border-white/10">
              <iframe
                title="Web Vantage office location"
                src="https://www.google.com/maps?q=San+Francisco&output=embed"
                className="h-64 w-full grayscale"
                loading="lazy"
              />
            </div>
          </Reveal>

          {/* form */}
          <Reveal delay={0.1}>
            {sent ? (
              <div className="flex h-full flex-col items-center justify-center rounded-3xl border border-vantage/40 bg-vantage/[0.06] p-12 text-center" data-testid="contact-success">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-vantage text-white"><Send className="h-7 w-7" /></div>
                <h3 className="mt-6 font-heading text-2xl font-semibold text-white">Message received!</h3>
                <p className="mt-3 max-w-sm text-slate-400">Thank you for reaching out. Our team will respond within 24 hours.</p>
                <button onClick={() => setSent(false)} className="btn-ghost mt-8">Send another message</button>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-3xl border border-white/10 bg-white/[0.03] p-8" data-testid="contact-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  <input required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name" />
                  <input required type="email" placeholder="Email address *" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email" />
                  <input placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="contact-phone" />
                  <input placeholder="Company" value={form.company} onChange={set("company")} className={inputCls} data-testid="contact-company" />
                  <select value={form.service} onChange={set("service")} className={inputCls} data-testid="contact-service">
                    <option value="">Service of interest</option>
                    {SERVICES.map((s) => <option key={s} value={s} className="bg-ink-900">{s}</option>)}
                  </select>
                  <select value={form.budget} onChange={set("budget")} className={inputCls} data-testid="contact-budget">
                    <option value="">Estimated budget</option>
                    {BUDGETS.map((b) => <option key={b} value={b} className="bg-ink-900">{b}</option>)}
                  </select>
                </div>
                <textarea required rows={5} placeholder="Tell us about your project *" value={form.message} onChange={set("message")} className="mt-4 w-full rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm text-white placeholder:text-slate-500 focus:border-vantage focus:outline-none" data-testid="contact-message" />
                <button type="submit" disabled={loading} className="btn-primary mt-6 w-full disabled:opacity-70" data-testid="contact-submit">
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</> : <>Send message <Send className="h-4 w-4" /></>}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}
